import { Routes } from '@angular/router';
import { LayoutComponent } from './layout/layout/layout.component';
import { authGuard } from './auth/guards/auth.guard';
import { permissionGuard } from './auth/guards/permission.guard';

export const routes: Routes = [
  // Auth routes - accessible without layout
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then(m => m.AuthModule)
  },
  // Payment successful route - accessible without layout
  {
    path: 'payment-success',
    loadComponent: () => import('./shared/components/payment-successful/payment-successful.component').then(c => c.PaymentSuccessfulComponent)
  },
  // Wallet topup callback - accessible without layout
  {
    path: 'wallet/topup-success',
    loadComponent: () => import('./modules/wallet/wallet-topup-callback/wallet-topup-callback.component').then(c => c.WalletTopupCallbackComponent)
  },
  // Registration product checkout - accessible without authentication
  {
    path: 'registration/checkout-payment',
    loadComponent: () => import('./modules/products/checkout-payment/checkout-payment.component').then(c => c.CheckoutPaymentComponent)
  },
  // Main application routes - protected by auth guard
  {
    path: '',
    component: LayoutComponent,
    canActivate: [authGuard, permissionGuard],
    children: [
      {
        path: '',
        redirectTo: 'modules',
        pathMatch: 'full'
      },
      {
        path: 'modules',
        loadComponent: () => import('./modules/modules-landing/modules-landing.component').then(c => c.ModulesLandingComponent)
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./modules/dashboard/dashboard.component').then(c => c.DashboardComponent)
      },
      // Request Reset Password page
      {
        path: 'request-reset-password',
        loadComponent: () => import('./shared/components/reset-password-dialog/reset-password-dialog.component').then(c => c.ResetPasswordDialogComponent)
      },
      // Change password route inside main layout
      {
        path: 'change-password',
        loadComponent: () => import('./modules/profile/change-password/change-password.component').then(c => c.ChangePasswordComponent)
      },
      {
        path: 'mlm-tree-view',
        redirectTo: 'hierarchy',
        pathMatch: 'full'
      },
      {
        path: 'user-tree-view',
        redirectTo: 'hierarchy',
        pathMatch: 'full'
      },
      {
        path: 'products',
        loadChildren: () => import('./modules/products/products.module').then(m => m.ProductsModule)
      },
      {
        path: 'hierarchy',
        loadChildren: () => import('./modules/hierarchy/hierarchy.module').then(m => m.HierarchyModule)
      },
      {
        path: 'categories',
        loadComponent: () => import('./modules/Master/categories/categories.component').then(c => c.CategoriesComponent)
      },
      {
        path: 'statuses',
        loadChildren: () => import('./modules/status-management/status-management.routes').then(r => r.STATUS_MANAGEMENT_ROUTES)
      },
      // Rewards Routes
      {
        path: 'rewards',
        loadComponent: () => import('./modules/rewards/components/rewards-dashboard.component').then(c => c.RewardsDashboardComponent)
      },
      {
        path: 'rewards/my-rewards',
        loadComponent: () => import('./modules/rewards/components/my-rewards.component').then(c => c.MyRewardsComponent)
      },
      {
        path: 'rewards/points',
        loadComponent: () => import('./modules/rewards/components/reward-points-dashboard.component').then(c => c.RewardPointsDashboardComponent)
      },
      {
        path: 'admin/rewards',
        loadComponent: () => import('./modules/rewards/components/rewards-management.component').then(c => c.RewardsManagementComponent)
      },
      {
        path: 'admin/rewards/conversions',
        loadComponent: () => import('./modules/rewards/components/reward-conversions-admin.component').then(m => m.RewardConversionsAdminComponent)
      },
      // Bonuses Routes
      {
        path: 'bonuses',
        loadComponent: () => import('./modules/bonuses/components/bonus-dashboard.component').then(c => c.BonusDashboardComponent)
      },
      {
        path: 'bonuses/my-bonuses',
        loadComponent: () => import('./modules/bonuses/components/my-bonuses.component').then(c => c.MyBonusesComponent)
      },
      {
        path: 'admin/bonuses',
        loadComponent: () => import('./modules/bonuses/components/bonus-management.component').then(c => c.BonusManagementComponent)
      },
      {
        path: 'admin/bonuses/manual-entry',
        loadComponent: () => import('./modules/bonuses/components/manual-bonus-entry-list.component').then(c => c.ManualBonusEntryListComponent)
      },
      {
        path: 'admin/bonuses/user-bonus-log',
        loadComponent: () => import('./modules/bonuses/components/user-bonus-log-list.component').then(c => c.UserBonusLogListComponent)
      },
      {
        path: 'admin/bonuses/rules',
        loadComponent: () => import('./modules/bonuses/components/bonus-rules-management.component').then(c => c.BonusRulesManagementComponent)
      },
      // Commission Routes
      {
        path: 'commission',
        loadComponent: () => import('./modules/commission/components/commission-dashboard.component').then(c => c.CommissionDashboardComponent)
      },
      {
        path: 'commission/history',
        loadComponent: () => import('./modules/commission/components/commission-history.component').then(c => c.CommissionHistoryComponent)
      },
      {
        path: 'admin/commission',
        loadComponent: () => import('./modules/commission/components/commission-management.component').then(c => c.CommissionManagementComponent)
      },
      // Reports Routes
      {
        path: 'reports',
        loadComponent: () => import('./modules/reports/components/user-reports.component').then(c => c.UserReportsComponent)
      },
      {
        path: 'admin/reports',
        loadComponent: () => import('./modules/reports/components/admin-reports.component').then(c => c.AdminReportsComponent)
      },
      {
        path: 'product-management',
        loadChildren: () => import('./modules/product-management/product-management.routes').then(r => r.PRODUCT_MANAGEMENT_ROUTES)
      },
      {
        path: 'warehouse-management',
        loadChildren: () => import('./modules/warehouse-management/warehouse-management.routes').then(r => r.WAREHOUSE_ROUTES)
      },
      {
        path: 'profile/image-upload',
        loadComponent: () => import('./shared/components/image-uploader/image-uploader.component').then(c => c.ImageUploaderComponent)
      },
      {
        path: 'profile/relationships',
        loadChildren: () => import('./modules/profile/relationship-management/relationship-management.module').then(m => m.RelationshipManagementModule)
      },
      {
        path: 'profile',
        loadComponent: () => import('./modules/profile/profile.component').then(c => c.ProfileComponent)
      },
      {
        path: 'profile/kyc',
        loadComponent: () => import('./modules/profile/kyc/kyc.component').then(c => c.KycComponent)
      },
      
      // Wallet Routes
      {
        path: 'wallet',
        loadChildren: () => import('./modules/wallet/wallet.module').then(m => m.WalletModule)
      },
      
      // Admin Routes
      {
        path: 'admin',
        children: [
          {
            path: 'dashboard',
            loadComponent: () => import('./modules/dashboard/dashboard.component').then(c => c.DashboardComponent)
          },
          // Admin Approval Routes
          {
            path: 'approvals',
            loadComponent: () => import('./modules/admin/approvals/admin-approval-dashboard.component').then(c => c.AdminApprovalDashboardComponent)
          },
          {
            path: 'approvals/users',
            loadComponent: () => import('./modules/admin/approvals/components/user-registration/user-registration.component').then(c => c.UserRegistrationComponent)
          },
          {
            path: 'approvals/products',
            loadComponent: () => import('./modules/admin/approvals/components/product-approvals/product-approvals.component').then(c => c.ProductApprovalsComponent)
          },

          {
            path: 'approvals/withdrawals',
            loadComponent: () => import('./modules/admin/approvals/components/withdrawal-requests/withdrawal-requests.component').then(c => c.WithdrawalRequestsComponent)
          },

          {
            path: 'approvals/kyc',
            loadComponent: () => import('./modules/admin/approvals/components/kyc-verification/kyc-verification.component').then(c => c.KycVerificationComponent)
          },
          {
            path: 'approvals/bonus',
            loadComponent: () => import('./modules/admin/approvals/components/bonus-approvals/bonus-approvals.component').then(c => c.BonusApprovalsComponent)
          },
          {
            path: 'approvals/orders',
            loadComponent: () => import('./modules/admin/approvals/components/order-approvals/order-approvals.component').then(c => c.OrderApprovalsComponent)
          },
          {
            path: 'approvals/cancellations',
            loadComponent: () => import('./modules/admin/cancel-approval/cancel-approval.component').then(c => c.CancelApprovalComponent)
          },
          {
            path: 'approvals/adjustments',
            loadComponent: () => import('./modules/admin/adjustment-approval/adjustment-approval.component').then(c => c.AdjustmentApprovalComponent)
          },
          {
            path: 'approvals/returns',
            loadComponent: () => import('./modules/admin/return-approval/return-approval.component').then(c => c.ReturnApprovalComponent)
          },
          {
            path: 'approvals/replacements',
            loadComponent: () => import('./modules/admin/replacement-approval/replacement-approval.component').then(c => c.ReplacementApprovalComponent)
          },
          {
            path: 'approvals/refunds',
            loadComponent: () => import('./modules/admin/refund-processing/refund-processing.component').then(c => c.RefundProcessingComponent)
          },
           {
            path: 'approvals/bank',
            loadComponent: () => import('./modules/admin/approvals/components/bank-approval/bank-approval.component').then(c => c.BankApprovalComponent)
          },
          {
            path: 'approvals/wallet-adjustments',
            loadComponent: () => import('./modules/wallet/cash-deposit-approval/cash-deposit-approval.component').then(c => c.default)
          },
          {
            path: 'approvals/payment-status',
            loadComponent: () => import('./modules/admin/payment-management/components/payment-responses/payment-responses.component').then(c => c.PaymentResponsesComponent)
          },
          {
            path: 'approval-configuration',
            loadComponent: () => import('./modules/Master/approval-configuration/approval-configuration.component')
          },
          {
            path: 'user-management',
            loadChildren: () => import('./modules/user-management/user-management.routes').then(r => r.USER_MANAGEMENT_ROUTES)
          },
          // Order Management Routes
          {
            path: 'orders',
            loadChildren: () => import('./modules/admin/order-management/order-management.routes').then(r => r.ORDER_MANAGEMENT_ROUTES)
          },
          // Payment Management Routes
          {
            path: 'payments',
            loadChildren: () => import('./modules/admin/payment-management/payment-management.routes').then(r => r.PAYMENT_MANAGEMENT_ROUTES)
          },
          {
            path: 'bulk-upload',
            loadComponent: () => import('./modules/admin/bulk-upload/bulk-upload.component').then(c => c.BulkUploadComponent)
          },
          // System Configuration
          {
            path: 'configuration',
            loadChildren: () => import('./modules/configuration/configuration.routes').then(r => r.CONFIG_ROUTES)
          },
          {
            path: 'reviews',
            loadComponent: () => import('./modules/admin/reviews/admin-review-management.component').then(c => c.AdminReviewManagementComponent)
          },
          {
            path: '',
            redirectTo: 'approvals',
            pathMatch: 'full'
          }
        ]
      },
      // Master Routes
      {
        path: 'master',
        loadChildren: () => import('./modules/Master/master.routes').then(r => r.MASTER_ROUTES)
      },
      // Commission extended routes
      {
        path: 'commission/detail/:id',
        loadComponent: () => import('./modules/commission/components/commission-detail.component').then(c => c.CommissionDetailComponent)
      },
      {
        path: 'commission/levels',
        loadComponent: () => import('./modules/commission/components/commission-level-breakdown.component').then(c => c.CommissionLevelBreakdownComponent)
      },
      {
        path: 'commission/wallet-credits',
        loadComponent: () => import('./modules/commission/components/commission-wallet-credits.component').then(c => c.CommissionWalletCreditsComponent)
      },
      {
        path: 'commission/simulation',
        loadComponent: () => import('./modules/commission/components/commission-simulation.component').then(c => c.CommissionSimulationComponent)
      },
      {
        path: 'admin/commission/approval',
        loadComponent: () => import('./modules/commission/components/commission-approval.component').then(c => c.CommissionApprovalComponent)
      },
      {
        path: 'admin/commission/payout',
        loadComponent: () => import('./modules/commission/components/commission-payout.component').then(c => c.CommissionPayoutComponent)
      },
      {
        path: 'admin/commission/engine',
        loadComponent: () => import('./modules/commission/components/commission-calculation-engine.component').then(c => c.CommissionCalculationEngineComponent)
      },
      {
        path: 'admin/commission/logs',
        loadComponent: () => import('./modules/commission/components/commission-logs.component').then(c => c.CommissionLogsComponent)
      },
      {
        path: 'admin/commission/reports',
        loadComponent: () => import('./modules/commission/components/commission-reports.component').then(c => c.CommissionReportsComponent)
      },
      // Bonuses extended routes
      {
        path: 'bonuses/leaderboard',
        loadComponent: () => import('./modules/bonuses/components/bonus-leaderboard.component').then(c => c.BonusLeaderboardComponent)
      },
      {
        path: 'bonuses/history',
        loadComponent: () => import('./modules/bonuses/components/bonus-history.component').then(c => c.BonusHistoryComponent)
      },
      {
        path: 'admin/bonuses/approval',
        loadComponent: () => import('./modules/bonuses/components/bonus-approval.component').then(c => c.BonusApprovalComponent)
      },
      {
        path: 'admin/bonuses/disbursement',
        loadComponent: () => import('./modules/bonuses/components/bonus-disbursement.component').then(c => c.BonusDisbursementComponent)
      },
      {
        path: 'admin/bonuses/criteria-builder',
        loadComponent: () => import('./modules/bonuses/components/bonus-criteria-builder.component').then(c => c.BonusCriteriaBuilderComponent)
      },
      {
        path: 'admin/bonuses/cycle-setup',
        loadComponent: () => import('./modules/bonuses/components/bonus-cycle-setup.component').then(c => c.BonusCycleSetupComponent)
      },
      {
        path: 'admin/bonuses/reports',
        loadComponent: () => import('./modules/bonuses/components/bonus-reports.component').then(c => c.BonusReportsComponent)
      },
      // Rewards extended routes
      {
        path: 'rewards/catalog',
        loadComponent: () => import('./modules/rewards/components/reward-catalog.component').then(c => c.RewardCatalogComponent)
      },
      {
        path: 'rewards/redemption',
        loadComponent: () => import('./modules/rewards/components/reward-redemption.component').then(c => c.RewardRedemptionComponent)
      },
      {
        path: 'rewards/achievements',
        loadComponent: () => import('./modules/rewards/components/reward-achievements.component').then(c => c.RewardAchievementsComponent)
      },
      {
        path: 'admin/rewards/catalog',
        loadComponent: () => import('./modules/rewards/components/admin-reward-catalog.component').then(c => c.AdminRewardCatalogComponent)
      },
      {
        path: 'admin/rewards/redemption-approval',
        loadComponent: () => import('./modules/rewards/components/reward-redemption-approval.component').then(c => c.RewardRedemptionApprovalComponent)
      },
      {
        path: 'admin/rewards/dispatch',
        loadComponent: () => import('./modules/rewards/components/reward-dispatch-tracking.component').then(c => c.RewardDispatchTrackingComponent)
      },
      {
        path: 'admin/rewards/points-rules',
        loadComponent: () => import('./modules/rewards/components/reward-points-rules.component').then(c => c.RewardPointsRulesComponent)
      },
      {
        path: 'admin/rewards/reports',
        loadComponent: () => import('./modules/rewards/components/reward-reports.component').then(c => c.RewardReportsComponent)
      },
      // Rule Engine routes
      {
        path: 'rule-engine',
        loadChildren: () => import('./modules/rule-engine/rule-engine.routes').then(r => r.RULE_ENGINE_ROUTES)
      },
      // Under construction / Page not found route - inside layout
      {
        path: 'under-construction',
        loadComponent: () => import('./shared/components/under-construction/under-construction.component').then(c => c.UnderConstructionComponent)
      },
      // Catch-all route for unknown paths within authenticated area
      {
        path: '**',
        loadComponent: () => import('./shared/components/under-construction/under-construction.component').then(c => c.UnderConstructionComponent)
      }
      // Add more routes here as needed
    ]
  }
];
